import {useCallback, useMemo, useState} from 'react'
import type {ConvertToRulesetPreview, ConvertToRulesetPreviewWithoutId, IUpdatePreviews, PreviewPayload} from '../types'

interface useEditablePreviews extends IUpdatePreviews {
  editablePreviews: ConvertToRulesetPreview[]
  initializePreviews: (initialPreviews: PreviewPayload) => void
}

const convertPayloadToMap = (previews: PreviewPayload): Map<number, ConvertToRulesetPreviewWithoutId> => {
  const mappedPreviews: Array<[number, ConvertToRulesetPreviewWithoutId]> = Object.entries(previews).map(
    ([id, preview]) => [Number(id), preview],
  )
  return new Map(mappedPreviews)
}

export function useEditablePreviews(previews?: PreviewPayload): useEditablePreviews {
  const [editablePreviews, setEditablePreviews] = useState(() => convertPayloadToMap(previews ?? {}))
  const initializePreviews = useCallback(
    (initialPreviews: PreviewPayload) => setEditablePreviews(convertPayloadToMap(initialPreviews)),
    [],
  )

  const togglePreview: useEditablePreviews['togglePreview'] = useCallback(({id}) => {
    setEditablePreviews(currentPreviews => {
      const preview = currentPreviews.get(id)
      if (!preview) {
        return currentPreviews
      }

      const newMap = new Map(currentPreviews)

      newMap.set(id, {
        ...preview,
        isEnabled: !preview.isEnabled,
      })
      return newMap
    })
  }, [])

  const setRulesetName: useEditablePreviews['setRulesetName'] = useCallback(({id, rulesetIndex, newRulesetName}) => {
    setEditablePreviews(currentPreviews => {
      const preview = currentPreviews.get(id)
      if (!preview) {
        return currentPreviews
      }
      const foundRuleset = preview?.rulesets[rulesetIndex]
      if (!foundRuleset) {
        return currentPreviews
      }

      const newMap = new Map(currentPreviews)

      newMap.set(id, {
        ...preview,
        rulesets: [
          ...preview.rulesets.slice(0, rulesetIndex),
          {
            ...foundRuleset,
            name: newRulesetName,
          },
          ...preview.rulesets.slice(rulesetIndex + 1),
        ],
      })
      return newMap
    })
  }, [])

  return useMemo(
    () => ({
      editablePreviews: Array.from(
        editablePreviews.entries().map(([id, preview]) => ({id, ...preview})),
      ) satisfies ConvertToRulesetPreview[],
      initializePreviews,
      setRulesetName,
      togglePreview,
    }),
    [editablePreviews, initializePreviews, setRulesetName, togglePreview],
  )
}
